/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {SpreaderInfoRow} from '@ion/dls/src/components/assetdetails/current/SpreaderInfoCard';
import {
  formatData,
  formatGroundValue,
  getAccumulatorDataLable,
  getAccumulatorDataValue,
  getMssStatus
} from '../../../manufacturer/spreaderUtils';
import {
  getNoValue,
  getOtherDetailsCard,
  getRemainingRows,
  splitMeasurement,
  SpreaderLayout,
  toCard,
  toRow
} from './spreaderLayoutShared';

const MASTER_SWITCH_STATUS = 'mss';

// the Dickey-john layout's slots, by the reading(s) each one shows - the air
// temp reads with the road temp, when reported ...
const DICKEY_JOHN_KEYS = {
  status             : MASTER_SWITCH_STATUS,
  tiles              : ['channeltargetApplicationRate', 'channelactualApplicationRate', 'gateHeight'],
  conditions         : ['groundSpeed', 'roadTemperature'],
  optionalConditions : ['airTemperature'],
  liquid             : ['channel2targetApplicationRate', 'channel2actualApplicationRate'],
  liquidTotal        : 'tankLevel'
};

// the readings the Dickey-john layout's slots show ...
const DICKEY_JOHN_USED_KEYS = Object.values(DICKEY_JOHN_KEYS).flat();

// the Dickey-john flow rate readings keyed like legacy's
// DickeyJohnCardListContent labels them - single readings by their own key
// (`mss`, `groundSpeed`, `gateHeight`...), channel rates by
// `<channel><type>` (`channeltargetApplicationRate`,
// `channel2actualApplicationRate`...) ...
const getDickeyJohnReadings = (measures: GenericObject) => (
  (measures?.flowRate ?? []).reduce(function(readings: GenericObject, data: GenericObject) {
    const keys = Object.keys(data ?? {});
    if (keys.length === 1) {
      const [key] = keys;
      readings[key] = key === MASTER_SWITCH_STATUS ? getMssStatus(data[key]?.trim?.()) : formatGroundValue(data);
    } else if (data?.channel) {
      readings[`${data.channel.split('_').join('').toLowerCase()}${data.type}`] = formatData(data);
    }
    return readings;
  }, {})
);

// the Dickey-john accumulator totals (everything in `measures` besides
// `flowRate`), labelled and valued like DickeyJohnCardListContent ...
const getDickeyJohnTotalsRows = (measures: GenericObject): SpreaderInfoRow[] => {
  const {flowRate, ...accumulatorData} = measures ?? {};
  return Object.values(accumulatorData).flatMap(function(settings: GenericObject) {
    return Object.values(settings ?? {}).flatMap(function(data: GenericObject[]) {
      return (Array.isArray(data) ? data : []).map(function(datum: GenericObject) {
        const {type, value, units, setting} = datum ?? {};
        return {
          label : getAccumulatorDataLable(setting ?? '', type, units),
          value : String(getAccumulatorDataValue(setting ?? '', value, units))
        };
      });
    });
  });
};

/**
 * Maps a Dickey-john message's measures onto the current theme Spreader
 * accordion's Spreader Type 2 layout: Master Switch Status, Granular Target
 * / Actual Rate and Gate Height tiles, a Conditions card (ground speed, road
 * temp and any air temp), a Liquid card (liquid target / actual rate, tank
 * level), a Totals card of the accumulator totals and an Other Details card
 * for any other flow rate reading.
 *
 * ```typescript
 *
 *   import {getDickeyJohnLayout} from './dickeyJohnLayout';
 *   ...
 *
 *   const layout = getDickeyJohnLayout(measures);
 *
 * ```
 *
 * @param   measures The Dickey-john measures.
 * @returns          The accordion layout.
 */
export const getDickeyJohnLayout = (measures: GenericObject): SpreaderLayout => {
  const readings           = getDickeyJohnReadings(measures);
  const label              = (key: string) => translate(`tracking:spreader.DICKEY_JOHN.${key}`);
  const tile               = (key: string) => ({label: label(key), ...splitMeasurement(readings[key])});
  const row                = (key: string) => toRow(label(key), readings[key]);
  const reportedConditions = DICKEY_JOHN_KEYS.optionalConditions.filter(function(key) {
    return !isNil(readings[key]);
  });
  const otherRows          = getRemainingRows({
    readings,
    usedKeys : DICKEY_JOHN_USED_KEYS,
    label,
    format   : function(key) {
      return readings[key];
    }
  });
  return {
    status          : readings[DICKEY_JOHN_KEYS.status] ?? getNoValue(),
    statusLabel     : label(DICKEY_JOHN_KEYS.status),
    tilesFitContent : true,
    tiles           : DICKEY_JOHN_KEYS.tiles.map(tile),
    cards           : [
      {
        title : translate('tracking:spreader.current.conditions'),
        rows  : [...DICKEY_JOHN_KEYS.conditions, ...reportedConditions].map(row)
      },
      {
        title : translate('tracking:spreader.current.liquid'),
        rows  : DICKEY_JOHN_KEYS.liquid.map(row),
        total : row(DICKEY_JOHN_KEYS.liquidTotal)
      },
      ...toCard(translate('tracking:spreader.current.totals'), getDickeyJohnTotalsRows(measures)),
      ...getOtherDetailsCard(otherRows)
    ]
  };
};
